import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FloppyDisk, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL_SPECS = ["Extensions", "Renovations", "Brickwork", "Carpentry", "Roofing", "Plastering", "Landscaping", "Kitchens", "Bathrooms", "Loft Conversions"];

export default function Settings() {
    const { user, updateProfile } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "",
        business_name: "",
        bio: "",
        location: "",
        phone: "",
        slug: "",
        specialties: [],
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || "",
                business_name: user.business_name || "",
                bio: user.bio || "",
                location: user.location || "",
                phone: user.phone || "",
                slug: user.slug || "",
                specialties: user.specialties || [],
            });
        }
    }, [user]);

    const toggleSpec = (s) => {
        setForm((f) => ({
            ...f,
            specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateProfile(form);
            toast.success("Profile updated");
            nav("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
            <button onClick={() => nav(-1)} className="text-overline text-neutral-600 hover:text-black inline-flex items-center gap-2 mb-6">
                <ArrowLeft size={14} /> Back
            </button>
            <div className="text-overline text-[#E63946] mb-2">Profile settings</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none uppercase mb-10">
                Edit profile.
            </h1>

            <form onSubmit={submit} className="space-y-5" data-testid="settings-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Your name</label>
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="input-field"
                            data-testid="settings-name-input"
                        />
                    </div>
                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Business</label>
                        <input
                            value={form.business_name}
                            onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                            className="input-field"
                            data-testid="settings-business-input"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-overline text-neutral-700 block mb-2">URL handle (slug)</label>
                    <div className="flex items-center bg-secondary">
                        <span className="px-3 text-xs text-neutral-500 font-mono-accent">/b/</span>
                        <input
                            value={form.slug}
                            onChange={(e) => setForm({ ...form, slug: e.target.value })}
                            className="input-field"
                            data-testid="settings-slug-input"
                        />
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">Your public profile lives at this address.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Location</label>
                        <input
                            value={form.location}
                            onChange={(e) => setForm({ ...form, location: e.target.value })}
                            className="input-field"
                            placeholder="Bristol, UK"
                            data-testid="settings-location-input"
                        />
                    </div>
                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Phone</label>
                        <input
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="input-field"
                            placeholder="07000 000000"
                            data-testid="settings-phone-input"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-overline text-neutral-700 block mb-2">Bio</label>
                    <textarea
                        rows={5}
                        value={form.bio}
                        onChange={(e) => setForm({ ...form, bio: e.target.value })}
                        className="input-field resize-none"
                        placeholder="Tell customers about your business…"
                        data-testid="settings-bio-input"
                    />
                </div>

                <div>
                    <label className="text-overline text-neutral-700 block mb-3">Specialties</label>
                    <div className="flex flex-wrap gap-2">
                        {ALL_SPECS.map((s) => (
                            <button
                                type="button"
                                key={s}
                                onClick={() => toggleSpec(s)}
                                data-testid={`spec-toggle-${s}`}
                                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${
                                    form.specialties.includes(s)
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-black border-neutral-300 hover:border-black"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                <button type="submit" disabled={saving} className="btn-accent mt-6" data-testid="settings-save-btn">
                    <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save profile"}
                </button>
            </form>
        </div>
    );
}
